import { restClient } from '@/Core/Services/RestClientService'
import { GetRestUrlTask } from '@/Core/Tasks/Resources/GetRestUrlTask'
/**
 * Task для перезавантаження даних поточного ресурсу з API
 * Використовується для оновлення даних в page mode після редагування в drawer
 * 
 * @param {Object} params
 * @param {Object} params.crudContext - контекст CRUD ресурсу
 * 
 * @example
 * ```typescript
 * // У onClose callback після редагування
 * crudContext.actions.reloadData()
 * ```
 */
export const ReloadDataActionHandlerTask = async ({ crudContext }: { crudContext: any }) => {
    // Перевіряємо що це detail context з formContext
    if (crudContext.contextType !== 'detail' || !crudContext.formContext) {
        console.warn('[ReloadDataActionHandlerTask] Can only reload data for detail context with formContext')
        return
    }

    // Отримуємо ID поточного ресурсу
    const primaryFieldCode = crudContext.resource?.primaryFieldCode || 'uuid'
    const itemId = crudContext.formContext.values?.[primaryFieldCode]

    if (!itemId) {
        console.warn('[ReloadDataActionHandlerTask] No itemId found in formContext.values')
        return
    }

    try {
        // Встановлюємо стан завантаження
        crudContext.formContext.setIsLoading(true)

        // Завантажуємо свіжі дані з API
        const freshData = await restClient.get(
            GetRestUrlTask(crudContext.resource.restUrl, 'get', { id: itemId }) + '?permissions=true',
            {
                headers: {
                    'X-LOCALE': crudContext.formContext?.currentLanguage || 'uk_UA',
                },
            },
            {
                forceRefresh: true, // Примусово виконуємо новий запит, ігноруючи кеш
            }
        )
        
        // Викликаємо події перед оновленням даних
        if (crudContext.eventManager) {
            crudContext.eventManager.emit('onResponseRequest', {
                initialValues: freshData,
                crudContext,
            })

            // Викликаємо специфічну подію залежно від режиму
            if (crudContext.formContext.isEditable) {
                crudContext.eventManager.emit('onResponseEditRequest', {
                    initialValues: freshData,
                    crudContext,
                })
            } else {
                crudContext.eventManager.emit('onResponseViewRequest', {
                    initialValues: freshData,
                    crudContext,
                })
            }
        }

        // Оновлюємо дані у формі
        crudContext.formContext.setInitialValues(freshData)
        crudContext.formContext.setStorage('responseData', { ...freshData })
        crudContext.formContext.setIsLoading(false)
    } catch (error) {
        console.error('[ReloadDataActionHandlerTask] Failed to reload data:', error)
        crudContext.formContext?.setIsLoading(false)
        throw error
    }
}
